import { useRef, type MouseEvent, type ReactNode } from "react";

type Props = {
    children: ReactNode;
    className?: string;
    spotlightColor?: string;
    radius?: number;
};

/**
 * Glass card that follows the cursor with a soft brand-lime highlight.
 * Pure CSS variables + pointer events — no JS reflows on the hot path.
 */
export function SpotlightCard({
    children,
    className = "",
    spotlightColor = "rgba(204, 255, 0, 0.13)",
    radius = 320,
}: Props) {
    const ref = useRef<HTMLDivElement>(null);

    const onMouseMove = (e: MouseEvent<HTMLDivElement>) => {
        const el = ref.current;
        if (!el) return;
        const rect = el.getBoundingClientRect();
        el.style.setProperty("--mx", `${e.clientX - rect.left}px`);
        el.style.setProperty("--my", `${e.clientY - rect.top}px`);
    };

    return (
        <div
            ref={ref}
            onMouseMove={onMouseMove}
            className={`relative overflow-hidden group ${className}`}
            style={
                {
                    "--spot": spotlightColor,
                    "--radius": `${radius}px`,
                } as React.CSSProperties
            }
        >
            <div
                aria-hidden
                className="pointer-events-none absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-300"
                style={{
                    background:
                        "radial-gradient(var(--radius) circle at var(--mx) var(--my), var(--spot), transparent 60%)",
                }}
            />
            <div className="relative z-10 h-full">{children}</div>
        </div>
    );
}
